import { Box, Divider, Menu, MenuItem, MenuList, Stack, Typography } from '@mui/material';
import { useCallback, useMemo, type MouseEventHandler, type ReactNode } from 'react';
import { LanguageType } from '../../../@types';
import { USER_ROLES } from '../../../constants/roles';
import { useUserData } from '../../../queries';
import { useRoles } from '../../../hooks/useRoles';
import useLocalization from '../../../hooks/useLocalization';
import useBreakpoints from '../../../hooks/useBreakpoints';
import { AccountMenuButton } from './AccountMenuButton';
import { useAccountMenu } from '../../../hooks/useAccountMenu';
import { FormattedBalance } from '../../atoms/FormattedBalance';
import useAuthForms from '../../../hooks/useAuthForms';
import { useLanguage } from '../../../localization/LanguageInstanceProvider';
import { useAvailableLanguages } from '../../../hooks/useAvailableLanguages';
import AccountAccordionItem from '../../atoms/AccountAccordionItem';
import { preventAutoEmailLink } from '../../../utils';
import { getAgentAccountMenuSkinStyles } from '../../../styles/components/agentAccountMenu';
import { useSkinStyles } from '../../../hooks/useSkinStyles';
import { useAccountMenuFields } from '../../../hooks/components/useAccountMenuFields';
import useProductAvailability from '../../../hooks/useProductAvailability';

import { mergeSx } from '../../../utils/styles';

const getRoleLabel = (role: string) => {
  switch (role) {
    case USER_ROLES.ADMIN:
      return 'Administrator ID';
    case USER_ROLES.MANAGER:
      return 'Manager ID';
    case USER_ROLES.AGENT:
      return 'Agent ID';
    default:
      return 'User ID';
  }
};

type AgentAccountMenuTriggerProps = {
  isOpen: boolean;
  onClick: MouseEventHandler<HTMLButtonElement>;
  showUnreadBadge: boolean;
};

type AgentAccountMenuProps = {
  showUnreadBadge?: boolean;
  showSummary?: boolean;
  renderTrigger?: (props: AgentAccountMenuTriggerProps) => ReactNode;
};

const AgentAccountMenu = ({ showUnreadBadge = true, showSummary = true, renderTrigger }: AgentAccountMenuProps) => {
  const { isMobile } = useBreakpoints();
  const { role, isPlayer } = useRoles();
  const { isCasinoOnlyProduct } = useProductAvailability();
  const { t } = useLocalization();
  const { openRegisterBOUserForm } = useAuthForms();
  const { setSelectedLanguage } = useLanguage();
  const availableLanguages = useAvailableLanguages();
  const { data: userSettings } = useUserData();

  const {
    anchorEl,
    copySuccess,
    isMenuOpen,
    preferredUsername,
    userId,
    handleAccountMenuOpen,
    handleAccountMenuClose,
    handleLogoutClick,
    handleCopyId,
    handleMenuItemClick,
  } = useAccountMenu();

  const { getMenuFields } = useAccountMenuFields();

  const handleLanguageChange = useCallback(
    async (value: LanguageType) => {
      await setSelectedLanguage(value);
      handleAccountMenuClose();
    },
    [setSelectedLanguage, handleAccountMenuClose]
  );

  const availableMenuList = useMemo(
    () => getMenuFields(handleMenuItemClick, openRegisterBOUserForm, handleLanguageChange, handleLogoutClick),
    [getMenuFields, handleMenuItemClick, openRegisterBOUserForm, handleLanguageChange, handleLogoutClick]
  );

  const playerMenuItems = availableMenuList.filter((item) => !['userList', 'createNewUser'].includes(item.key));
  const agentMenuItems = availableMenuList.filter((item) => item.key !== 'messages');
  const menuItems = isPlayer ? playerMenuItems : agentMenuItems;
  const idLabel = getRoleLabel(role);
  const styles = useSkinStyles(getAgentAccountMenuSkinStyles(copySuccess));
  const showPlayerBonusCredits = isPlayer && !isCasinoOnlyProduct;

  const userIdDisplay = (userId && userId.length >= 6 ? userId.slice(-6) : userId || '------').toUpperCase();
  const flattenUserSettings = { ...(userSettings ?? {}), ...(userSettings?.sportsbookConfig ?? {}) };

  const trigger = renderTrigger ? (
    renderTrigger({ onClick: handleAccountMenuOpen, showUnreadBadge, isOpen: isMenuOpen })
  ) : (
    <AccountMenuButton onClick={handleAccountMenuOpen} showUnreadBadge={showUnreadBadge} />
  );

  return (
    <>
      <Stack direction={showSummary && isMobile ? 'column-reverse' : 'row'} spacing={showSummary ? 0.5 : 0}>
        {showSummary && (
          <Box>
            <Typography variant='h6' sx={styles.username}>
              {preferredUsername}
            </Typography>
            <Typography variant='body3' sx={styles.balance}>
              {isPlayer ? <FormattedBalance balanceType='main' /> : role}
            </Typography>
          </Box>
        )}
        {trigger}
      </Stack>
      {isMenuOpen && (
        <Menu
          anchorEl={anchorEl}
          open
          onClose={handleAccountMenuClose}
          elevation={2}
          sx={styles.menuContainer}
          slotProps={{
            paper: {
              sx: styles.menuPaper,
            },
          }}
        >
          {!isPlayer && (
            <Box sx={styles.copyContainer} onClick={() => handleCopyId(userId ?? '')}>
              <Stack sx={styles.copyText}>
                <Typography variant='body2' sx={styles.copyLabel}>
                  {copySuccess ? t('idSuccessfullyCopied') : idLabel}
                </Typography>
                <Typography
                  variant='h5'
                  sx={mergeSx(
                    {
                      fontWeight: 600,
                    },
                    styles.copyId
                  )}
                >
                  {userIdDisplay}
                </Typography>
              </Stack>
            </Box>
          )}

          <Divider sx={styles.divider} />

          <MenuItem sx={styles.balanceContainer} onClick={() => handleMenuItemClick('/current-balance')}>
            <Stack>
              <Typography variant='body2'>{preventAutoEmailLink(preferredUsername)}</Typography>
              <Typography
                variant='h5'
                color='primary'
                sx={{
                  fontWeight: 600,
                }}
              >
                <FormattedBalance balanceType='main' />
                {showPlayerBonusCredits && (
                  <Stack
                    sx={{
                      pt: 0.5,
                    }}
                  >
                    <Typography>{t('bonus')}</Typography>
                    <FormattedBalance balanceType='bonus' />
                  </Stack>
                )}
              </Typography>
            </Stack>
          </MenuItem>

          <MenuList>
            {menuItems.map(({ key, action }) =>
              key === 'language' ? (
                <AccountAccordionItem
                  key={key}
                  userData={flattenUserSettings}
                  menuItems={availableLanguages}
                  passedKeyValue={key}
                  action={action}
                  menuItemSx={styles.languageMenuItem}
                />
              ) : (
                <MenuItem key={key} disableRipple sx={styles.menuItem} onClick={action as () => void}>
                  <Typography
                    variant='body2'
                    sx={{
                      fontWeight: 600,
                    }}
                  >
                    {t(key)}
                  </Typography>
                </MenuItem>
              )
            )}
          </MenuList>
        </Menu>
      )}
    </>
  );
};

export default AgentAccountMenu;
